import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../AuthContext";
import api from "../../api";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet-async";

const helmetNode = (
  <Helmet>
    <title>Modifier mon profil | LMFP Photography</title>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

const EditUser = () => {
  const navigate = useNavigate();
  const { user, authLoading, refreshUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(null);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    birthday: "",
    location: "",
    avatar: "",
  });

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path instanceof File) return URL.createObjectURL(path);
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  useEffect(() => {
    if (!authLoading && user) {
      setFormData({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        birthday: user.birthday || "",
        location: user.location || "",
        avatar: user.avatar || "",
      });
      setLoading(false);
    }
    if (!authLoading && !user) {
      setError("Vous devez être connecté pour voir votre profil.");
      setLoading(false);
    }
  }, [authLoading, user]);

  useEffect(() => {
    if (!formData.avatar) {
      setAvatarPreview(null);
      return;
    }
    if (formData.avatar instanceof File) {
      const objectUrl = URL.createObjectURL(formData.avatar);
      setAvatarPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setAvatarPreview(getImageUrl(formData.avatar));
  }, [formData.avatar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setFormData((prev) => ({ ...prev, avatar: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError("");
      await api.put("/users/me", {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        birthday: formData.birthday || null,
        location: formData.location,
      });
      if (formData.avatar instanceof File) {
        const avatarData = new FormData();
        avatarData.append("avatar", formData.avatar);
        await api.put("/users/me/avatar", avatarData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await refreshUser();
      toast.success("Profil mis à jour avec succès !");
      setTimeout(() => navigate("/profile"), 1200);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(" ");
        setError(msgs);
      } else {
        setError(err.response?.data?.message || "Erreur lors de la mise à jour du profil.");
      }
      toast.error("Erreur lors de la mise à jour du profil.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        {helmetNode}
        <div className="flex min-h-screen items-center justify-center bg-slate-950">
          <p className="text-xl font-semibold text-slate-100">Chargement...</p>
        </div>
      </>
    );
  }

  if (error && !formData.firstname && !formData.lastname && !formData.email) {
    return (
      <>
        {helmetNode}
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-4">
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-center text-red-300">
            {error}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {helmetNode}
      <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
        <div className="mx-auto max-w-4xl">
          <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
            <div className="p-8 md:p-10">
              <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
                Profil utilisateur
              </p>
              <h1 className="mb-3 text-3xl font-bold md:text-4xl">
                Modifier mon profil
              </h1>
              <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
                Mettez à jour vos informations personnelles, votre localité et
                votre photo de profil depuis cet espace.
              </p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
            {error && (
              <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">Avatar</h2>
                {avatarPreview && (
                  <div className="mb-6 flex flex-col items-center">
                    <p className="mb-3 text-sm font-medium text-slate-300">
                      {formData.avatar instanceof File ? "Nouvel avatar" : "Avatar actuel"}
                    </p>
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="h-28 w-28 rounded-full border-4 border-slate-700 object-cover shadow-lg"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="avatar" className="mb-2 block text-sm font-medium text-slate-200">
                    Modifier l'avatar
                  </label>
                  <input
                    type="file"
                    id="avatar"
                    name="avatar"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">Informations personnelles</h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="firstname" className="mb-2 block text-sm font-medium text-slate-200">Prénom</label>
                    <input type="text" id="firstname" name="firstname" value={formData.firstname} onChange={handleChange} required
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="lastname" className="mb-2 block text-sm font-medium text-slate-200">Nom</label>
                    <input type="text" id="lastname" name="lastname" value={formData.lastname} onChange={handleChange} required
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-200">Email</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="birthday" className="mb-2 block text-sm font-medium text-slate-200">Date de naissance</label>
                    <input type="date" id="birthday" name="birthday" value={formData.birthday} onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="location" className="mb-2 block text-sm font-medium text-slate-200">Localité</label>
                    <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} placeholder="Ex : Bruxelles" required
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button type="submit" disabled={submitting}
                  className="w-full rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                  {submitting ? "Mise à jour..." : "Mettre à jour"}
                </button>
                <button type="button" onClick={() => navigate("/profile")}
                  className="w-full rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition hover:bg-slate-800 sm:w-auto">
                  Annuler
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </>
  );
};

export default EditUser;